import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  User, 
  Shield, 
  Users, 
  Building2, 
  MapPin, 
  BookOpen,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  RefreshCw,
  Key,
  Crown,
  Briefcase,
  Loader2
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

//dummy push test
const PermissionBrowser = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [permissionFilter, setPermissionFilter] = useState('');

  // Fetch users on search
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/dev/user-permissions/', {
        params: { search: searchTerm, limit: 50 }
      });
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchUsers]);

  // Fetch user details
  const fetchUserDetails = async (user) => {
    setSelectedUser(user);
    setDetailsLoading(true);
    try {
      const response = await api.post('/api/dev/user-permissions/', {
        user_id: user.id
      });
      if (response.data.success) {
        setUserDetails(response.data);
        // Expand all groups by default
        const expanded = {};
        response.data.groups.forEach(g => {
          expanded[g.id] = true;
        });
        setExpandedGroups(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to fetch user permissions');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Filter permissions based on search
  const filterPermissions = (permissions) => {
    if (!permissionFilter) return permissions;
    const filter = permissionFilter.toLowerCase();
    return permissions.filter(p => 
      p.codename?.toLowerCase().includes(filter) ||
      p.name?.toLowerCase().includes(filter) ||
      p.full_name?.toLowerCase().includes(filter) ||
      (typeof p === 'string' && p.toLowerCase().includes(filter))
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Key className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Permission Browser</h3>
        </div>
        <button
          onClick={fetchUsers}
          className="btn btn-ghost btn-sm"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List Panel */}
        <div className="card p-4 lg:col-span-1">
          <div className="mb-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 pr-4"
              />
            </div>
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
              </div>
            ) : users.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No users found
              </div>
            ) : (
              users.map(user => (
                <button
                  key={user.id}
                  onClick={() => fetchUserDetails(user)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selectedUser?.id === user.id
                      ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                      : 'border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-gray-900/40 dark:hover:bg-white/[0.04]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`rounded-full p-2 ${
                      user.is_superuser ? 'bg-warning-50 dark:bg-warning-500/10' : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      {user.is_superuser ? (
                        <Crown className="h-4 w-4 text-warning-600 dark:text-warning-400" />
                      ) : (
                        <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.email}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{user.role || 'No role'}</span>
                        {user.is_internal && (
                          <span className="badge badge-info">
                            Internal
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Details Panel */}
        <div className="card p-6 lg:col-span-2">
          {!selectedUser ? (
            <div className="flex h-full flex-col items-center justify-center py-16 text-center text-gray-500 dark:text-gray-400">
              <Shield className="mb-3 h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Select a user to view permissions</p>
              <p className="mt-1 text-sm">Click on a user from the list to see their details</p>
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
          ) : userDetails ? (
            <div className="space-y-6">
              {/* User Info Header */}
              <div className="flex items-start justify-between border-b border-gray-200 pb-4 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`rounded-full p-3 ${
                    userDetails.user.is_superuser ? 'bg-warning-50 dark:bg-warning-500/10' : 'bg-primary-50 dark:bg-primary-900/30'
                  }`}>
                    {userDetails.user.is_superuser ? (
                      <Crown className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                    ) : (
                      <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">{userDetails.user.email}</h4>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="badge badge-gray">
                        {userDetails.user.role || 'No role'}
                      </span>
                      {userDetails.user.is_superuser && (
                        <span className="badge badge-warning">
                          Superuser
                        </span>
                      )}
                      {userDetails.user.is_staff && (
                        <span className="badge badge-brand">
                          Staff
                        </span>
                      )}
                      {userDetails.user.is_internal && (
                        <span className="badge badge-info">
                          Internal
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-xl border border-gray-200 bg-white p-3 text-center dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{userDetails.summary.total_permission_count}</p>
                  <p className="stat-label mt-0.5 justify-center"><Key className="h-3.5 w-3.5 text-primary-500" />Total Permissions</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-3 text-center dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{userDetails.summary.group_count}</p>
                  <p className="stat-label mt-0.5 justify-center"><Users className="h-3.5 w-3.5 text-info-500" />Groups</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-3 text-center dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{userDetails.summary.sponsor_count}</p>
                  <p className="stat-label mt-0.5 justify-center"><Building2 className="h-3.5 w-3.5 text-success-500" />Sponsors</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-3 text-center dark:border-gray-700 dark:bg-gray-800">
                  <p className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-gray-100">{userDetails.summary.site_count}</p>
                  <p className="stat-label mt-0.5 justify-center"><MapPin className="h-3.5 w-3.5 text-warning-500" />Sites</p>
                </div>
              </div>

              {/* Permission Search */}
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter permissions..."
                  value={permissionFilter}
                  onChange={(e) => setPermissionFilter(e.target.value)}
                  className="input pl-10 pr-4"
                />
              </div>

              {/* Tabs */}
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {/* Groups with Permissions */}
                {userDetails.groups.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      <Users className="w-4 h-4" />
                      <span>Groups ({userDetails.groups.length})</span>
                    </div>
                    {userDetails.groups.map(group => (
                      <div key={group.id} className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => toggleGroup(group.id)}
                          className="flex w-full items-center justify-between bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:bg-gray-900/40 dark:hover:bg-white/[0.04]"
                        >
                          <div className="flex items-center space-x-2">
                            {expandedGroups[group.id] ? (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )}
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{group.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({filterPermissions(group.permissions).length} permissions)
                            </span>
                          </div>
                        </button>
                        <AnimatePresence>
                          {expandedGroups[group.id] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="p-3 bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                  {filterPermissions(group.permissions).map((perm, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs dark:bg-gray-900/60"
                                    >
                                      <Check className="h-3 w-3 flex-shrink-0 text-success-500" />
                                      <span className="truncate text-gray-700 dark:text-gray-300" title={perm.full_name}>
                                        {perm.codename}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                )}

                {/* Direct Permissions */}
                {userDetails.direct_permissions.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      <Key className="w-4 h-4" />
                      <span>Direct Permissions ({userDetails.direct_permissions.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                      {filterPermissions(userDetails.direct_permissions).map((perm, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1 rounded-md bg-primary-50 px-2 py-1 text-xs dark:bg-primary-900/20"
                        >
                          <Check className="h-3 w-3 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                          <span className="truncate text-gray-700 dark:text-gray-300" title={perm.full_name}>
                            {perm.codename}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sponsors */}
                {userDetails.sponsors.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      <Building2 className="w-4 h-4" />
                      <span>Sponsors ({userDetails.sponsors.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                      {userDetails.sponsors.map(sponsor => (
                        <span
                          key={sponsor.id}
                          className="badge badge-success"
                        >
                          {sponsor.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sites */}
                {userDetails.sites.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      <MapPin className="w-4 h-4" />
                      <span>Sites ({userDetails.sites.length})</span>
                    </div>
                    <div className="space-y-1 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 max-h-40 overflow-y-auto">
                      {userDetails.sites.map(site => (
                        <div
                          key={site.id}
                          className="flex items-center justify-between rounded-md bg-warning-50 px-2 py-1 text-xs dark:bg-warning-500/10"
                        >
                          <span className="text-gray-700 dark:text-gray-300">{site.name}</span>
                          {site.study && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{site.study}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Studies */}
                {userDetails.studies.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      <BookOpen className="w-4 h-4" />
                      <span>Studies ({userDetails.studies.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                      {userDetails.studies.map(study => (
                        <span
                          key={study.id}
                          className="badge badge-info"
                        >
                          {study.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Permissions (collapsed by default) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                    <Shield className="w-4 h-4" />
                    <span>All Permissions ({filterPermissions(userDetails.all_permissions).length})</span>
                  </div>
                  <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {filterPermissions(userDetails.all_permissions).map((perm, idx) => (
                        <div
                          key={idx}
                          className="truncate rounded-md bg-white px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          title={perm}
                        >
                          {perm}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PermissionBrowser;
